import {
  AccessToken,
  AdminSignInResponse,
  AdminUserRole,
  Region,
  RegionConfig,
} from "../types";

const FW_AUTH_KEY = "fw_auth";

type AuthInfo = AdminSignInResponse;

const storage = {
  /**
   * Gets authentication information from local storage if it exists.
   */
  loadAuthInfo: (): AuthInfo | null => {
    const authInfoString = localStorage.getItem(FW_AUTH_KEY);
    if (authInfoString) {
      try {
        return JSON.parse(authInfoString);
      } catch {
        // Looks like we couldn't properly parse the authInfoString!
        console.log("Could not properly parse authInfoString.");
      }
    }
    return null;
  },

  /**
   * Gets current admin user's roles from local storage if they exist.
   */
  loadRoles: (): AdminUserRole[] => {
    return storage.loadAuthInfo()?.roles ?? [];
  },

  /**
   * Gets current data region configs from local storage if they exist.
   */
  loadRegionConfigs: (): RegionConfig[] => {
    return storage.loadAuthInfo()?.data_region_configs ?? [];
  },

  /**
   * Gets current data regions from local storage if they exist.
   */
  enumerateRegions: (): Region[] => {
    return storage.loadRegionConfigs().map((config) => config.region);
  },

  /**
   * Gets config for a given region from local storage if it exists.
   */
  getConfigForRegion: (region: Region): RegionConfig => {
    const regionConfig = storage
      .loadRegionConfigs()
      .find((c) => c.region === region);

    if (!regionConfig) {
      throw new Error("Can't find region config for region " + region);
    }

    return regionConfig;
  },

  /**
   * Gets API base URL for a given region from local storage if it exists.
   */
  getApiBaseUrlForRegion: (region: Region): string => {
    const regionConfig = storage.getConfigForRegion(region);

    return regionConfig.api_base_url;
  },

  /**
   * Gets system status dashboard URL for a given region from local storage if it exists.
   */
  getSystemStatusDashboardUrlForRegion: (region: Region): string | null => {
    const regionConfig = storage.getConfigForRegion(region);

    return regionConfig.system_status_dashboard_url;
  },

  /**
   * Removes authentication information from local storage if it exists.
   */
  removeAuthInfo: (): void => {
    localStorage.removeItem(FW_AUTH_KEY);
  },

  /**
   * Sets authentication information in local storage.
   */
  storeAuthInfo: (authObject: AuthInfo): void => {
    try {
      localStorage.setItem(FW_AUTH_KEY, JSON.stringify(authObject));
    } catch {
      // Looks like we couldn't set ${FW_AUTH_KEY} information in local storage.
      console.log(`Could not set ${FW_AUTH_KEY} info in local storage.`);
    }
  },

  /**
   * Updates access token in local storage.
   */
  updateAccessToken: (accessToken: AccessToken): void => {
    updateAuthInfoValue("access_token", accessToken);
  },

  /**
   * Updates roles in local storage.
   */
  updateRoles: (roles: AdminUserRole[]): void => {
    updateAuthInfoValue("roles", roles);
  },

  /**
   * Updates data region configs in local storage. Refreshed on token refresh
   * so a stale config (e.g. from a session that predates a config change)
   * self-heals without forcing the user to log out and back in.
   */
  updateRegionConfigs: (regionConfigs: RegionConfig[]): void => {
    updateAuthInfoValue("data_region_configs", regionConfigs);
  },
};

/**
 * Update a given key-value pair of auth info in local storage.
 */
const updateAuthInfoValue = (key: keyof AuthInfo, value: any) => {
  try {
    const authInfo = storage.loadAuthInfo();
    if (authInfo) {
      const authInfoWithUpdatedValue = {
        ...authInfo,
        [key]: value,
      };
      storage.storeAuthInfo(authInfoWithUpdatedValue);
    }
  } catch {
    console.log(
      `Could not update ${key} with value ${value} in local storage.`,
    );
  }
};

export default storage;
